/**
 * General Process designer (obj_type "G") — a process that GENERATES
 * transactions: source SQL → calculations (E/C/S/L) → preview → persist into
 * a target table or an existing transaction object (header+detail, grouped
 * by a unique transaction id).
 *
 * Works exactly like the transaction/visual designers: embedded in the
 * workbench "Design" tab (with Save / dirty guard / agent auto-refresh) and
 * paired with a "Live Preview" run surface (see RunPanel).
 */ 

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft, Loader2, Maximize2, Minimize2, Save, Workflow,
} from 'lucide-react'
import type { EmbeddedDesignerProps } from './embeddedDesigner'
import {
  fetchProjectConnections, loadProcessObject, saveProcessObject,
} from '@/api/genprocess'
import { generateBusinessLogicFile } from '@/api/designers'
import { SqlBuilderButton } from '@twasta/page-runtime/designerKit'
import { useProjectStore } from '@/store/projectStore'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { useAgentRefresh } from '@/hooks/useAgentRefresh'
import DiscardChangesModal from '@/components/common/DiscardChangesModal'
import type { ExecutionMode, ExternalConnection, GenProcess } from './genprocess/types'
import {
  blankProcess, executionModeOf, executionModePatch, sanitizeProcess,
} from './genprocess/types'
import TargetPanel from './genprocess/TargetPanel'
import FileSourcePanel from './genprocess/FileSourcePanel'
import RunPanel from './genprocess/RunPanel'
import {
  Field, SectionHeader, cardStyle, hintStyle, inputStyle, monoAreaStyle,
  selectStyle,
} from './genprocess/ui'
// Vendored (source gen-process) feature components — reused verbatim, themed
// to twasta via GenprocMuiProvider and routed through the vendor/api adapter.
import GenprocMuiProvider from './genprocess/vendor/MuiProvider'
import { RunApi, setGenprocCtx } from './genprocess/vendor/api'
import CalculationGrid from './genprocess/vendor/CalculationGrid'
import LookupEditor from './genprocess/vendor/LookupEditor'
import CriteriaForm from './genprocess/vendor/CriteriaForm'
import EditableColumnsField from './genprocess/EditableColumnsField'
import ValidationsPanel from './genprocess/ValidationsPanel'
import ObjectSchedulesPanel from './scheduler/ObjectSchedulesPanel'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'

type Tab = 'source' | 'calculations' | 'lookups' | 'target' | 'preferences' | 'validations' | 'run' | 'schedules'

const TABS: { key: Tab; label: string }[] = [
  { key: 'source', label: 'Source' },
  { key: 'calculations', label: 'Calculations' },
  { key: 'lookups', label: 'Look-ups' },
  { key: 'target', label: 'Target' },
  { key: 'preferences', label: 'Preferences' },
  { key: 'validations', label: 'Validations' },
  { key: 'run', label: 'Review & Execute' },
  // The DOOR onto scheduling: the schedulers that run this process (derived,
  // never stored here) and every run of it, however it started.
  { key: 'schedules', label: 'Schedules & runs' },
]

interface Props extends EmbeddedDesignerProps {
  /** Embedded in the workbench: open a business-logic file in the object's own
      "Code" tab instead of a new browser tab. */
  onOpenCodeFile?: (relPath: string | null) => void
}

export default function GenProcessDesignerPage({
  embedded = false,
  hostFullscreen = false,
  embedProject,
  embedObjName,
  onDirtyChange,
  registerSave,
  onSaved,
  onOpenCodeFile,
}: Props = {}) {
  const params = useParams<{ project: string; objName: string }>()
  const project = embedProject ?? params.project
  useRunOnlyRedirect(project)
  const objName = embedObjName ?? params.objName
  const navigate = useNavigate()
  const location = useLocation()
  const projectName = project || ''
  const objectName = objName || ''
  const { currentProject, selectProject } = useProjectStore()

  useEffect(() => {
    if (project && currentProject?.application.folder_name !== project) {
      selectProject(project)
    }
  }, [project, currentProject, selectProject])

  const [proc, setProc] = useState<GenProcess>(() => blankProcess(objectName))
  const [criteriaValues, setCriteriaValues] = useState<Record<string, unknown>>({})
  const [connections, setConnections] = useState<ExternalConnection[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { canEditObjectsOrLoading: canEdit } = useProjectPermissions()
  const [tab, setTab] = useState<Tab>('source')
  const [embedFullscreen, setEmbedFullscreen] = useState(false)

  // Dirty tracking — snapshot of the saved model.
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null)
  const isDirty = savedSnapshot !== null && JSON.stringify(proc) !== savedSnapshot
  const { guard, showDiscardModal, confirmDiscard, cancelDiscard } =
    useUnsavedChangesGuard(isDirty)

  // Reload when the AI agent edits the model file (deferred while dirty).
  const agentRefresh = useAgentRefresh(projectName, isDirty)

  useEffect(() => {
    let cancelled = false
    if (!projectName || !objectName) return
    setLoading(true)
    loadProcessObject(projectName, objectName)
      .then(({ content }) => {
        if (cancelled) return
        const loaded = content
          ? sanitizeProcess(content, objectName)
          : blankProcess(objectName)
        setProc(loaded)
        setCriteriaValues({ ...(loaded.default_params || {}) })
        setSavedSnapshot(JSON.stringify(loaded))
      })
      .catch((err) => {
        console.error('Failed to load process:', err)
        toast.error('Failed to load process')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [projectName, objectName, agentRefresh])

  useEffect(() => {
    if (!projectName) return
    let cancelled = false
    fetchProjectConnections(projectName)
      .then((conns) => { if (!cancelled) setConnections(conns) })
      .catch(() => { if (!cancelled) setConnections([]) })
    return () => { cancelled = true }
  }, [projectName])

  const patch = useCallback((p: Partial<GenProcess>) => {
    setProc((prev) => ({ ...prev, ...p }))
  }, [])

  // Source/preview output columns captured from the last preview run on the
  // Review & Execute tab. Lifted to page level (RunPanel unmounts on tab
  // switch) so the Target tab's "MAPS FROM (SOURCE COLUMN)" dropdowns populate
  // — in Transaction mode there are no target_columns, so without these the
  // mapping pickers would be empty.
  const [previewColumns, setPreviewColumns] = useState<string[]>([])

  // Columns available to calc/target pickers: preview/source columns + target
  // sources + calc outputs.
  const availableColumns = useMemo(() => {
    const out: string[] = []
    const seen = new Set<string>()
    const add = (c: string) => {
      const k = c.toLowerCase()
      if (k && !seen.has(k)) { seen.add(k); out.push(k) }
    }
    for (const c of previewColumns) add(c)
    for (const c of proc.target_columns || []) add(c.source || c.name)
    for (const c of proc.calculations || []) add(c.col_name)
    return out
  }, [previewColumns, proc.target_columns, proc.calculations])

  // The run surface needs the freshest criteria defaults baked in.
  const procWithDefaults = useMemo<GenProcess>(() => {
    const dp: Record<string, string> = {}
    Object.entries(criteriaValues).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return
      dp[k] = Array.isArray(v) ? v.map(String).join(', ') : String(v)
    })
    return { ...proc, default_params: dp }
  }, [proc, criteriaValues])

  // Point the vendored components' ambient API context at this project (their
  // project-less calls — e.g. LookupsApi.list() — read it). Set during render so
  // it's in place before any child mounts and fires its data-fetch effect.
  if (projectName) setGenprocCtx({ project: projectName })

  // Candidate columns for the "editable columns" picker — the preview/source +
  // calc columns, plus any columns the transaction mapping reads (transaction
  // mode has no target_columns), plus anything already marked editable.
  const editableCandidates = useMemo(() => {
    const out = new Set(availableColumns)
    const tt = proc.transaction_target
    if (tt) {
      for (const f of tt.header_fields || []) if (f.source) out.add(f.source.toLowerCase())
      for (const arr of Object.values(tt.detail_fields || {})) {
        for (const f of arr) if (f.source) out.add(f.source.toLowerCase())
      }
    }
    if (Array.isArray(proc.editableColumns)) {
      for (const c of proc.editableColumns) out.add(c.toLowerCase())
    }
    for (const k of Object.keys(proc.editable_lookups || {})) out.add(k.toLowerCase())
    return [...out]
  }, [availableColumns, proc.transaction_target, proc.editableColumns, proc.editable_lookups])

  // 🔍 picker search for a criterion's reference table (vendored CriteriaForm).
  const fetchCriterionLookup = useCallback(
    async (criterion: string, search: string) =>
      (await RunApi.columnLookup({
        process: procWithDefaults, criterion, search, row: criteriaValues,
      })).rows,
    [procWithDefaults, criteriaValues],
  )

  // "Open in code editor" for the process hook / a business-logic item_change /
  // validation. Same flow the transaction designer uses: the backend creates
  // the stub if it doesn't exist yet (a descriptive header comment, in the
  // event's own language) and hands back the language-correct path, so we open
  // exactly that file rather than dropping the user at the source root. Runs
  // through `guard` because the stub is generated from the SAVED model.
  const openCode = useCallback((method: string) => {
    guard(async () => {
      if (!projectName) return
      let relPath: string | null = null
      const fn = (method || '').trim()
      if (fn && objectName) {
        try {
          const res = await generateBusinessLogicFile(projectName, objectName, fn, 'G')
          if (res?.path) relPath = res.path
          else toast('Could not locate a code file for this function; opening the editor.')
        } catch {
          // fall through to the project's source root
        }
      }
      if (embedded && onOpenCodeFile) {
        onOpenCodeFile(relPath)
        return
      }
      const url = relPath
        ? `/srceditor/${encodeURIComponent(projectName)}?file=${encodeURIComponent(relPath)}&root=src`
        : `/srceditor/${encodeURIComponent(projectName)}?root=src`
      window.open(url, '_blank', 'noopener')
    })
  }, [guard, projectName, objectName, embedded, onOpenCodeFile])

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!projectName || !objectName) return false
    setSaving(true)
    try {
      await saveProcessObject(projectName, objectName, procWithDefaults)
      setProc(procWithDefaults)
      setSavedSnapshot(JSON.stringify(procWithDefaults))
      toast.success('Process saved')
      // Tell the embedding shell the object now exists on disk: its Live
      // Preview tab is gated on that, answered once when the node was
      // selected, so a first save would otherwise leave the tab missing
      // until the node was reselected.
      onSaved?.()
      return true
    } catch (err) {
      console.error('Save failed', err)
      toast.error('Failed to save process')
      return false
    } finally {
      setSaving(false)
    }
  }, [projectName, objectName, procWithDefaults, onSaved])

  useEffect(() => {
    if (!embedded || !onDirtyChange) return
    onDirtyChange(isDirty)
    return () => onDirtyChange(false)
  }, [embedded, isDirty, onDirtyChange])
  useEffect(() => {
    if (!embedded || !registerSave) return
    registerSave(handleSave)
  }, [embedded, registerSave, handleSave])

  const handleBack = useCallback(() => {
    const selectedNodeId = (location.state as { selectedNodeId?: string })?.selectedNodeId
    guard(() => navigate('/workbench', { state: { selectedNodeId } }))
  }, [guard, location.state, navigate])

  const handleSaveAndContinue = useCallback(async () => {
    const ok = await handleSave()
    if (ok) confirmDiscard()
  }, [handleSave, confirmDiscard])

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100%' }}>
        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: 8 }}>Loading process…</span>
      </div>
    )
  }

  return (
    <div style={embedded && embedFullscreen ? {
      position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--color-bg)',
      display: 'flex', flexDirection: 'column', padding: '0 12px',
    } : {
      display: 'flex', flexDirection: 'column', height: '100%',
    }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '4px 0', flexShrink: 0,
      }}>
        <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
          {!embedded && (
            <button className="btn btn-ghost btn-sm" onClick={handleBack}>
              <ArrowLeft size={16} />
            </button>
          )}
          <Workflow size={16} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
          <div style={{
            minWidth: 0, fontSize: '0.9125rem', color: 'var(--color-text)',
            fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {projectName}
            <span style={{ opacity: 0.4, margin: '0 6px' }}>/</span>
            {objectName}
            <span style={{
              marginLeft: 8, fontSize: '0.8125rem', fontWeight: 400,
              color: 'var(--color-text-secondary)', opacity: 0.8,
            }}>
              general process
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit ? (
            <button
              className={`btn btn-sm ${isDirty ? 'btn-primary' : 'btn-secondary'}`}
              onClick={handleSave}
              disabled={saving}
              title={isDirty ? 'You have unsaved changes' : 'No changes to save'}
              style={isDirty ? { boxShadow: '0 0 0 3px rgba(76,141,255,0.35)' } : undefined}
            >
              {saving
                ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                : <Save size={14} />}
              {saving ? 'Saving…' : isDirty ? 'Save *' : 'Save'}
            </button>
          ) : (
            <ReadOnlyBadge />
          )}
          {embedded && !hostFullscreen && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setEmbedFullscreen((f) => !f)}
              title={embedFullscreen ? 'Restore' : 'Full screen'}
            >
              {embedFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: 4,
        borderTop: '1px solid var(--color-border)',
        borderBottom: '1px solid var(--color-border)',
        background: 'var(--color-bg-secondary)', padding: '0 8px',
      }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '8px 14px', border: 'none', background: 'transparent',
              borderBottom: tab === t.key
                ? '2px solid var(--color-accent)' : '2px solid transparent',
              fontSize: 14, fontWeight: tab === t.key ? 600 : 400,
              color: tab === t.key ? 'var(--color-text)' : 'var(--color-text-secondary)',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: 'var(--color-bg)' }}>
       <GenprocMuiProvider>
        {tab === 'source' && (
          <>
            <div style={cardStyle}>
              <SectionHeader>Where do the rows come from?</SectionHeader>
              <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>
                {([
                  ['sql', 'A database query', 'Run SQL against a connected database.'],
                  ['file', 'An imported CSV file', 'The operator picks a file each run.'],
                ] as const).map(([kind, label, help]) => (
                  <label key={kind}
                    style={{ display: 'flex', gap: 6, alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="gp-source-kind"
                      checked={(proc.source_kind || 'sql') === kind}
                      onChange={() => patch({ source_kind: kind })}
                      style={{ marginTop: 2 }}
                    />
                    <span>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{label}</div>
                      <div style={hintStyle}>{help}</div>
                    </span>
                  </label>
                ))}
              </div>

              {(proc.source_kind || 'sql') === 'file' ? (
                <FileSourcePanel project={projectName} process={proc} onChange={patch} />
              ) : (
              <>
              <Field label="Source database">
                <select
                  value={proc.source_connection_id || ''}
                  onChange={(e) => patch({ source_connection_id: e.target.value })}
                  style={selectStyle}
                >
                  <option value="">Project database (default)</option>
                  {connections.map((c) => (
                    <option key={c.CONNECTION_ID} value={c.CONNECTION_ID}>
                      {c.CONNECTION_NAME || c.CONNECTION_ID} ({c.DATABASE_TYPE || 'postgresql'})
                    </option>
                  ))}
                </select>
                <div style={hintStyle}>
                  Any connected database can be the source — including an
                  analytics store such as Dremio. Connections are managed in
                  the workbench Advanced tab.
                </div>
              </Field>

              <SectionHeader>Source SQL — the rows the process works on</SectionHeader>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                <SqlBuilderButton value={proc.source_sql || ''} mode="select" project={projectName || undefined}
                  target={proc.source_connection_id ? `conn:${proc.source_connection_id}` : 'project'}
                  title="Process source SQL"
                  onChange={(sql) => patch({ source_sql: sql })}
                  onTargetChange={(t) => patch({
                    source_connection_id: t.startsWith('conn:') ? t.slice(5) : t === 'project' ? '' : proc.source_connection_id,
                  })} />
              </div>
              <textarea
                value={proc.source_sql}
                onChange={(e) => patch({ source_sql: e.target.value })}
                spellCheck={false}
                style={monoAreaStyle}
                placeholder={'SELECT * FROM orders\nWHERE region = ?.region.string\n  AND order_date >= ?.start_date.date.@1stdayofmonth'}
              />
              <div style={hintStyle}>
                Runs against the <strong>project database</strong> by default.
                Use <code>?.NAME</code> for a typed prompt — append{' '}
                <code>.string</code> / <code>.date</code> / <code>.number</code>{' '}
                for the type and <code>.@today</code>, <code>.@1stdayofmonth</code>{' '}
                … for a dynamic default. The operator before the placeholder
                (<code>=</code>, <code>in</code>, <code>between</code>) decides
                whether it prompts for a value, a list, or a range.
              </div>
              </>
              )}
            </div>

            {(proc.source_kind || 'sql') !== 'file' && (
              <>
                <div style={cardStyle}>
                  <SectionHeader>Criteria — runtime prompts</SectionHeader>
                  <CriteriaForm
                    sourceSql={proc.source_sql}
                    values={criteriaValues}
                    onChange={setCriteriaValues}
                    labels={proc.criteria_labels}
                    lookups={proc.criteria_lookups}
                    onChangeLookups={(next) => patch({ criteria_lookups: next })}
                    fetchLookup={fetchCriterionLookup}
                  />
                </div>

                <div style={cardStyle}>
                  <Field label="Description (shown in lists; context for the AI agent)">
                    <textarea
                      value={proc.description || ''}
                      onChange={(e) => patch({ description: e.target.value })}
                      style={{ ...inputStyle, minHeight: 50, resize: 'vertical' }}
                    />
                  </Field>
                </div>
              </>
            )}
          </>
        )}

        {tab === 'calculations' && (
          <CalculationGrid
            calculations={proc.calculations}
            availableColumns={availableColumns}
            onChange={(next) => patch({ calculations: next })}
          />
        )}

        {tab === 'lookups' && (
          <LookupEditor
            process={procWithDefaults}
            params={criteriaValues}
            onChange={(lookups) => patch({ lookups })}
          />
        )}

        {tab === 'target' && (
          <div style={cardStyle}>
            <TargetPanel
              project={projectName}
              process={proc}
              availableColumns={availableColumns}
              connections={connections}
              onChange={patch}
              onOpenCode={openCode}
            />
          </div>
        )}

        {tab === 'preferences' && (() => {
          const mode = executionModeOf(proc)
          const MODE_OPTS: { key: ExecutionMode; label: string; hint: string }[] = [
            {
              key: 'direct', label: 'Directly process — no preview',
              hint: 'Execute runs the source SQL and writes every resulting row. The user never reviews the data.',
            },
            {
              key: 'preview', label: 'User previews the data',
              hint: 'Show the resulting rows for review; Execute then writes them all.',
            },
            {
              key: 'select', label: 'Make row selection',
              hint: 'Show the rows with checkboxes; Execute writes only the rows the user ticks. Execute stays hidden until rows are retrieved and a selection is made.',
            },
            {
              key: 'edit', label: 'Edit column values',
              hint: 'Show the rows and let the user edit cell values (with look-ups / item-change rules). Editing a cell selects its row; Execute writes only the selected rows (it stays hidden until a row is edited or ticked).',
            },
          ]
          return (
            <div style={cardStyle}>
              <SectionHeader>Execution mode — what the user does before Execute</SectionHeader>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {MODE_OPTS.map((o) => (
                  <label
                    key={o.key}
                    style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, cursor: 'pointer' }}
                  >
                    <input
                      type="radio"
                      name="gp-exec-mode"
                      checked={mode === o.key}
                      onChange={() => patch(executionModePatch(proc, o.key))}
                      style={{ marginTop: 2 }}
                    />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ fontWeight: mode === o.key ? 600 : 400 }}>{o.label}</span>
                      <div style={hintStyle}>{o.hint}</div>
                    </span>
                  </label>
                ))}
              </div>

              {mode === 'edit' && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--color-border)' }}>
                  <SectionHeader>Editable columns</SectionHeader>
                  <label style={{ marginRight: 14, fontSize: 13.5 }}>
                    <input
                      type="radio"
                      checked={proc.editableColumns === 'all' || !proc.editableColumns}
                      onChange={() => patch({ editableColumns: 'all' })}
                    /> All columns
                  </label>
                  <label style={{ fontSize: 13.5 }}>
                    <input
                      type="radio"
                      checked={Array.isArray(proc.editableColumns)}
                      onChange={() => patch({ editableColumns: [] })}
                    /> Selected columns
                  </label>
                  {Array.isArray(proc.editableColumns) && (
                    <EditableColumnsField
                      candidates={editableCandidates}
                      editableColumns={proc.editableColumns}
                      lookups={proc.editable_lookups || {}}
                      onChangeEditable={(next) => patch({ editableColumns: next })}
                      onChangeLookups={(next) => patch({ editable_lookups: next })}
                      itemChanges={proc.item_changes || {}}
                      onChangeItemChanges={(next) => patch({ item_changes: next })}
                      headerColumns={Object.keys(criteriaValues)}
                      project={projectName}
                      onOpenCode={openCode}
                    />
                  )}
                </div>
              )}
            </div>
          )
        })()}

        {tab === 'validations' && (
          <div style={cardStyle}>
            <ValidationsPanel
              validations={proc.validations || []}
              formColumns={editableCandidates}
              headerColumns={Object.keys(criteriaValues)}
              project={projectName}
              onChange={(next) => patch({ validations: next })}
              onOpenCode={openCode}
            />
          </div>
        )}

        {tab === 'schedules' && (
          <ObjectSchedulesPanel project={projectName} objName={objectName} kind="process" />
        )}

        {tab === 'run' && (
          <RunPanel
            project={projectName}
            process={procWithDefaults}
            criteriaValues={criteriaValues}
            onCriteriaChange={setCriteriaValues}
            onPatch={patch}
            onPreviewColumns={setPreviewColumns}
          />
        )}
       </GenprocMuiProvider>
      </div>

      <DiscardChangesModal
        open={showDiscardModal}
        onCancel={cancelDiscard}
        onDiscard={confirmDiscard}
        onSave={handleSaveAndContinue}
        saving={saving}
      />
    </div>
  )
}
